import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Fingerprint } from "lucide-react";
import { detectBrowserLang } from "@/lib/detect-lang";
import archLogo from "@/assets/arch-logo-bronze.webp";
import bgLanding from "@/assets/bg-landing.webp";
import AmbientParticles from "@/components/AmbientParticles";
import FloatingFog from "@/components/FloatingFog";

type Lang = "es" | "en";

const t = {
  es: {
    title: "El Zaguán",
    subtitle: "Club privado",
    emailPlaceholder: "Tu email",
    submit: "Entrar",
    switchLang: "English",
    pending: "Tu solicitud está en revisión. Te contactaremos pronto.",
    error: "Ocurrió un error. Intenta de nuevo.",
    passkey: "Passkey",
    or: "o",
  },
  en: {
    title: "El Zaguán",
    subtitle: "Private club",
    emailPlaceholder: "Your email",
    submit: "Enter",
    switchLang: "Español",
    pending: "Your request is under review. We'll contact you soon.",
    error: "Something went wrong. Please try again.",
    passkey: "Passkey",
    or: "or",
  },
};

const isValidEmail = (value: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

const Index = () => {
  const [lang, setLang] = useState<Lang>(detectBrowserLang());
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { session, member, loading: authLoading } = useAuth();
  const s = t[lang];

  // Auto-redirect authenticated members to /inicio
  useEffect(() => {
    if (!authLoading && session && member) {
      navigate("/inicio", { replace: true });
    }
  }, [authLoading, session, member, navigate]);

  // Detect WebAuthn support (any authenticator — platform, hybrid, or cross-device)
  useEffect(() => {
    setPasskeySupported(
      typeof PublicKeyCredential !== "undefined" &&
        typeof navigator.credentials?.get === "function",
    );
  }, []);

  const handlePasskeySignIn = async () => {
    setPasskeyLoading(true);
    setMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke("passkey-auth", {
        body: { action: "options" },
      });
      if (error || !data?.options) throw new Error("Failed to get options");

      const { startAuthentication } = await import("@simplewebauthn/browser");
      const assertion = await startAuthentication({
        optionsJSON: data.options,
      });

      const { data: verifyData, error: verifyErr } =
        await supabase.functions.invoke("passkey-auth", {
          body: {
            action: "verify",
            assertion,
            challenge_id: data.challenge_id,
          },
        });
      if (verifyErr || !verifyData?.verified) {
        throw new Error("Verification failed");
      }

      const { error: otpErr } = await supabase.auth.verifyOtp({
        token_hash: verifyData.token_hash,
        type: "email",
      });
      if (otpErr) throw otpErr;

      // AuthContext picks up via onAuthStateChange → redirect to /inicio
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "NotAllowedError") {
        // User cancelled the biometric prompt — not an error
      } else {
        setMessage(s.error);
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setEmailError(lang === "es" ? "Ingresa un correo válido" : "Enter a valid email");
      return;
    }
    setEmailError(null);

    setLoading(true);
    setMessage(null);

    try {
      const { data, error } = await supabase.functions.invoke("email-lookup", {
        body: { email: email.trim() },
      });

      if (error) throw error;

      switch (data.result) {
        case "active":
          navigate("/login", { state: { email: email.trim(), lang } });
          break;
        case "pending":
          setMessage(s.pending);
          break;
        case "not_found":
          navigate("/request", { state: { email: email.trim(), lang } });
          break;
      }
    } catch {
      setMessage(s.error);
    } finally {
      setLoading(false);
    }
  };

  // Show spinner while auth is restoring (prevents flash of landing page for authenticated users)
  if (authLoading) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-background">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="animate-grain flex h-[100dvh] flex-col items-center justify-center overflow-hidden bg-background px-4">
      {/* Background image */}
      <div
        className="fixed inset-0 z-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${bgLanding})` }}
      />
      <div className="fixed inset-0 z-0 bg-gradient-to-t from-background via-background/80 to-background/40" />

      <AmbientParticles />
      <FloatingFog />

      <button
        onClick={() => setLang(lang === "es" ? "en" : "es")}
        className="absolute right-6 top-6 z-10 text-sm font-light text-muted-foreground hover:text-foreground"
      >
        {s.switchLang}
      </button>

      {/* Arch container */}
      <div className="relative z-10 flex items-center justify-center">
        <img
          src={archLogo}
          alt=""
          aria-hidden="true"
          width={1024}
          height={1024}
          fetchPriority="high"
          className="pointer-events-none h-[43rem] sm:h-[49rem] md:h-[54rem] w-auto"
        />

        {/* Content centered inside the arch — vw-based width tracks the arch as it resizes */}
        <div className="absolute inset-0 grid grid-rows-[1fr_4fr] sm:grid-rows-[1fr_3fr]">
          <div />
          <div className="flex flex-col items-center justify-center pb-[8%]">
            <div className="w-[30vw] sm:w-auto sm:max-w-[14rem] md:max-w-[16rem] flex flex-col items-center">
              <h1 className="text-glow text-xl font-bold text-foreground sm:text-4xl md:text-5xl font-serif text-center">
                {s.title}
              </h1>
              <p className="mt-1 sm:mt-2 text-2xs sm:text-sm tracking-[0.15em] sm:tracking-[0.25em] uppercase text-muted-foreground font-light">
                {s.subtitle}
              </p>

              <form onSubmit={handleLookup} className="mt-3 sm:mt-8 w-full space-y-1.5 sm:space-y-3">
                <Input
                  type="email"
                  placeholder={s.emailPlaceholder}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(null);
                  }}
                  className="h-8 sm:h-12 text-center text-xs sm:text-base border-primary/20 bg-background/60 backdrop-blur-sm"
                  inputMode="email"
                  autoComplete="email"
                  disabled={loading}
                  required
                  maxLength={254}
                  aria-label={s.emailPlaceholder}
                />
                {emailError && (
                  <p className="text-center text-xs text-destructive">{emailError}</p>
                )}
                <Button
                  type="submit"
                  className="h-8 sm:h-12 w-full text-xs sm:text-base font-semibold hover:shadow-warm-glow-lg"
                  disabled={loading || !isValidEmail(email)}
                >
                  {loading ? <Loader2 className="animate-spin" /> : s.submit}
                </Button>
              </form>

              {passkeySupported && (
                <>
                  <div className="mt-2 sm:mt-3 flex items-center gap-1.5 sm:gap-2 w-full">
                    <div className="flex-1 border-t border-primary/20" />
                    <span className="text-2xs sm:text-xs text-muted-foreground font-light">
                      {s.or}
                    </span>
                    <div className="flex-1 border-t border-primary/20" />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePasskeySignIn}
                    disabled={loading || passkeyLoading}
                    className="mt-1.5 sm:mt-3 h-auto min-h-[2rem] sm:h-12 w-full whitespace-normal text-xs sm:text-base border-primary/20 py-1.5 sm:py-2"
                  >
                    {passkeyLoading ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <>
                        <Fingerprint className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 shrink-0" />
                        {s.passkey}
                      </>
                    )}
                  </Button>
                </>
              )}

              {message && (
                <p className="mt-4 text-center text-xs sm:text-sm text-muted-foreground font-light">
                  {message}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
